import React from "react";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
} from "../../apis/API";
import { strings } from "../../resources/strings";
import { resolveLocalizedText } from "../../utils/questionUtils";

const menu = strings.reportForm.questionMenu;

type RowStatus = "done" | "current" | "notStarted";

const STATUS_LABEL: Record<RowStatus, string> = {
  done: menu.statusDone,
  current: menu.statusCurrent,
  notStarted: menu.statusNotStarted,
};

// Decorative status glyphs (aria-hidden) — the text label conveys the meaning.
const STATUS_ICON: Record<RowStatus, string> = {
  done: "✓",
  current: "✎",
  notStarted: "○",
};

// Semantic status colors from the Tailwind palette (not Figma's iOS hexes).
// Shades are the lightest that clear 4.5:1 against the white panel at this text
// size: green-600 measured 3.30:1 and gray-400 2.54:1. blue-600 already passes
// at 5.17:1. The 1.4.3 exception for inactive components would cover the rows
// beyond the frontier, but a not-started row is enabled at the frontier itself,
// so the same token has to pass there.
const STATUS_COLOR: Record<RowStatus, string> = {
  done: "text-green-700",
  current: "text-blue-600",
  notStarted: "text-gray-500",
};

export interface QuestionListMenuProps {
  /** Supported questions in display order (`flow.questions`). */
  questions: LocalizedParticipantQuestion[];
  /** Index of the active question (`flow.index`). */
  currentIndex: number;
  /** Recorded-answer lookup (`flow.answerFor`) — drives the "Done" status. */
  answerFor: (questionId: string) => IdentifiableAnswerV3Input | undefined;
  /**
   * Render grouped section headers
   * (`agencyQuestionGroup.displaySectionGroupNames`). The backend only sets this
   * true for the in-order default form (no group "disruption"), so grouping by
   * consecutive section-name change is sufficient.
   */
  displaySectionGroupNames: boolean;
  /** Navigate the wizard to the chosen question. */
  onSelect: (index: number) => void;
}

/**
 * Section header for a question: its semantic group's localized name, falling
 * back to "General Questions" for custom / admin-added questions that carry no
 * default section. Isolated so the source field is a one-line swap if the
 * participant gateway ever exposes a dedicated section field.
 */
const getSectionName = (q: LocalizedParticipantQuestion): string =>
  resolveLocalizedText(q.semanticGroup?.localization) ?? menu.generalSection;

/**
 * Question List menu (Figma "iOS - Menu Expanded"). A sheet listing every
 * supported question with a completion-status indicator, letting the
 * participant jump back to any answered question (or the next one to answer).
 *
 * Navigation is intentionally back-only: questions are answered in order, so the
 * answered set is a leading prefix and the "frontier" (first unanswered) is the
 * only not-yet-answered question reachable. Questions beyond the frontier are
 * shown but disabled — you cannot skip ahead. Rendered as the body of a Headless
 * UI `PopoverPanel` (IHA-387) anchored to the toolbar's Question List button;
 * the Popover provides open/close, Escape, outside-click, and focus-return.
 */
const QuestionListMenu: React.FC<QuestionListMenuProps> = ({
  questions,
  currentIndex,
  answerFor,
  displaySectionGroupNames,
  onSelect,
}) => {
  const isAnswered = (q: LocalizedParticipantQuestion): boolean =>
    answerFor(q.id ?? "") !== undefined;

  const frontierRaw = questions.findIndex((q) => !isAnswered(q));
  const frontier = frontierRaw === -1 ? questions.length : frontierRaw;

  const statusOf = (
    q: LocalizedParticipantQuestion,
    index: number,
  ): RowStatus =>
    index === currentIndex ? "current" : isAnswered(q) ? "done" : "notStarted";

  // Tracks the previously rendered section so a header only appears when the
  // section changes (grouped mode). Mutated in document order by the map below.
  let prevSection: string | undefined;

  return (
    // Fills the enclosing PopoverPanel (a flex column, IHA-387) and scrolls the
    // question rows. There is no header/close row: the menu is dismissed by
    // selecting a question or clicking outside (the Popover backdrop), so the
    // scrollbar spans only the list. The card chrome (rounded/bg/shadow) lives
    // on the PopoverPanel because floating-ui sizes that element's max-height;
    // the toolbar's "Question List" button names it (the panel keeps an
    // aria-label for the accessible name).
    <div
      data-testid="question-list-items"
      className="min-h-0 flex-1 overflow-y-auto py-2"
    >
      {questions.map((q, index) => {
        const section = getSectionName(q);
        const showHeader = displaySectionGroupNames && section !== prevSection;
        prevSection = section;

        const status = statusOf(q, index);
        const navigable = index <= frontier;
        const text = resolveLocalizedText(q.localization) ?? "";

        return (
          <React.Fragment key={`${q.id ?? "q"}-${index}`}>
            {showHeader && (
              <h3 className="border-t border-gray-100 px-4 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-gray-500 first:border-t-0">
                {section}
              </h3>
            )}
            <button
              type="button"
              data-testid={`question-list-item-${index}`}
              disabled={!navigable}
              aria-current={status === "current" ? "step" : undefined}
              onClick={() => onSelect(index)}
              className={`flex min-h-[44px] w-full items-start gap-3 px-4 py-2 text-left disabled:cursor-not-allowed ${
                status === "notStarted" ? "text-gray-500" : "text-gray-900"
              }`}
            >
              <span aria-hidden="true" className="w-5 shrink-0 font-bold">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block break-words">{text}</span>
                <span
                  className={`flex items-center gap-1 text-xs ${STATUS_COLOR[status]}`}
                >
                  <span aria-hidden="true">{STATUS_ICON[status]}</span>
                  {STATUS_LABEL[status]}
                </span>
              </span>
              {navigable && (
                <span aria-hidden="true" className="shrink-0 text-gray-500">
                  ›
                </span>
              )}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default QuestionListMenu;
